// قائمة الدخل — بالتصميم الموحّد
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Box, Paper, Typography, CircularProgress } from '@mui/material';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import PercentIcon from '@mui/icons-material/Percent';

import accountingReportsApi from '../../api/accountingReportsApi';
import { ReportPage, ReportFilters, useReportDateRange, fmtMoney } from '../../components/Reports/ReportKit';
import { PrintSheet, PrintSummary, useReportPrint, useCompanyInfo, fmtDate } from '../../components/Reports/PrintKit';
import { FinancialSection, PrintFinancialSection, STATEMENT_ROUTE } from './FinancialSection';

export default function IncomeStatementReport() {
  const navigate = useNavigate();
  const range = useReportDateRange('year');
  const company = useCompanyInfo();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await accountingReportsApi.getIncomeStatement({ fromDate: range.dateFrom, toDate: range.dateTo });
      setData(res.data);
    } catch (err) {
      console.error(err);
      toast.error('فشل جلب قائمة الدخل');
    } finally {
      setLoading(false);
    }
  }, [range.dateFrom, range.dateTo]);

  useEffect(() => {
    const id = setTimeout(fetchReport, 300);
    return () => clearTimeout(id);
  }, [fetchReport]);

  const revenue = Number(data?.totalRevenue) || 0;
  const expense = Number(data?.totalExpense) || 0;
  const net = Number(data?.netIncome) || 0;
  const margin = revenue ? (net / revenue) * 100 : 0;
  const profit = net >= 0;

  const openStatement = (r) =>
    navigate(`${STATEMENT_ROUTE}?accountId=${r.accountId}&from=${range.dateFrom}&to=${range.dateTo}`);

  const printRef = useRef(null);
  const handlePrint = useReportPrint(printRef, 'قائمة-الدخل');

  return (
    <ReportPage
      title="قائمة الدخل"
      icon={<QueryStatsIcon />}
      period={range}
      actions={[
        { label: 'تحديث', icon: <RefreshIcon />, onClick: fetchReport, disabled: loading },
        { label: 'طباعة', icon: <PrintOutlinedIcon />, onClick: handlePrint, disabled: !data, primary: true },
      ]}
      stats={data ? [
        { label: 'إجمالي الإيرادات', value: fmtMoney(revenue), icon: <TrendingUpIcon />, color: 'success' },
        { label: 'إجمالي المصروفات', value: fmtMoney(expense), icon: <TrendingDownIcon />, color: 'error' },
        { label: 'هامش الربح', value: `${margin.toFixed(1)}%`, icon: <PercentIcon />, color: 'info' },
        { label: profit ? 'صافي الربح' : 'صافي الخسارة', value: fmtMoney(net), icon: <SavingsOutlinedIcon />, color: profit ? 'success' : 'error', highlight: true },
      ] : []}
      filters={
        <ReportFilters range={range} onApply={fetchReport} onReset={() => range.reset()} loading={loading} />
      }
    >
      {data && (
        <Box sx={{ display: 'none' }}>
          <PrintSheet ref={printRef} title="قائمة الدخل" period={`من ${fmtDate(range.dateFrom)} إلى ${fmtDate(range.dateTo)}`} company={company}>
            <PrintSummary items={[
              ['الإيرادات', fmtMoney(revenue)],
              ['المصروفات', fmtMoney(expense)],
              [profit ? 'صافي الربح' : 'صافي الخسارة', fmtMoney(net)],
              ['هامش الربح', `${margin.toFixed(1)}%`],
            ]} />
            <PrintFinancialSection title="الإيرادات" rows={data.revenues} total={revenue} />
            <PrintFinancialSection title="المصروفات" rows={data.expenses} total={expense} />
            <div style={{ border: '2px solid #111827', borderRadius: 8, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 800 }}>
              <span>{profit ? 'صافي الربح' : 'صافي الخسارة'}</span>
              <span style={{ direction: 'ltr' }}>{fmtMoney(net)}</span>
            </div>
          </PrintSheet>
        </Box>
      )}

      {loading && !data ? (
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 260 }}><CircularProgress /></Box>
      ) : data && (
        <Box sx={{ display: 'grid', gap: 1.5, opacity: loading ? .6 : 1, transition: 'opacity .2s' }}>
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, alignItems: 'start' }}>
            <FinancialSection title="الإيرادات" rows={data.revenues || []} total={revenue} color="#059669"
              icon={<TrendingUpIcon />} onRowClick={openStatement} />
            <FinancialSection title="المصروفات" rows={data.expenses || []} total={expense} color="#e11d48"
              icon={<TrendingDownIcon />} onRowClick={openStatement} />
          </Box>

          {/* النتيجة */}
          <Paper elevation={0} sx={{
            borderRadius: '16px', p: { xs: 2, sm: 2.5 }, color: '#fff',
            display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
            background: profit ? 'linear-gradient(120deg,#047857,#10b981)' : 'linear-gradient(120deg,#be123c,#f43f5e)',
          }}>
            <SavingsOutlinedIcon sx={{ fontSize: 40, opacity: .9 }} />
            <Box sx={{ flex: 1, minWidth: 180 }}>
              <Typography sx={{ fontWeight: 800, fontSize: 18 }}>{profit ? 'صافي الربح' : 'صافي الخسارة'}</Typography>
              <Typography sx={{ fontSize: 13.5, opacity: .9 }}>
                الإيرادات {fmtMoney(revenue)} − المصروفات {fmtMoney(expense)}
              </Typography>
            </Box>
            <Typography sx={{ fontWeight: 900, fontSize: { xs: 26, sm: 32 }, direction: 'ltr' }}>{fmtMoney(net)}</Typography>
          </Paper>
        </Box>
      )}
    </ReportPage>
  );
}
